import { useCallback, useEffect, useRef, useState } from "react";

type SupportModalProps = {
	iframeSrc: string;
	open: boolean;
	setOpen: (open: boolean) => void;
	showCallSupport?: boolean;
	callSupportAccessCode?: number;
	isMobileLayout?: boolean;
};

export function SupportModal({
	iframeSrc,
	open,
	setOpen,
	showCallSupport,
	callSupportAccessCode,
	isMobileLayout,
}: SupportModalProps) {
	const [supportModalIframeSrc, setSupportModalIframeSrc] = useState(iframeSrc);
	const [supportModalState, setSupportModalState] = useState<"hidden" | "modal" | "chat">("hidden");
	const [chatDimensions, setChatDimensions] = useState({ width: 300, height: 380 });
	const supportModalIframeRef = useRef<HTMLIFrameElement>(null);
	const isChat = supportModalState === "chat";

	const postToIframe = (data: object) => {
		const iframeWindow = supportModalIframeRef?.current?.contentWindow;
		if (iframeWindow) {
			iframeWindow.postMessage(data, "*");
		}
	};

	const openSupportModal = () => postToIframe({ subject: "OPEN_SUPPORT_MODAL" });

	const setAccessCode = useCallback(() => {
		if (showCallSupport && callSupportAccessCode) {
			postToIframe({
				subject: "SET_ACCESS_CODE",
				accessCode: callSupportAccessCode,
			});
		}
	}, [showCallSupport, callSupportAccessCode]);

	const setMobileLayout = () =>
		postToIframe({
			subject: "SET_MOBILE_LAYOUT",
			isMobile: isMobileLayout,
		});

	useEffect(() => {
		if (open) openSupportModal();
	}, [open]);

	useEffect(() => {
		const url = new URL(iframeSrc);
		if (showCallSupport) url.searchParams.set("showCallSupport", "true");
		setSupportModalIframeSrc(url.toString());
	}, [showCallSupport]);

	useEffect(() => {
		setAccessCode();
	}, [callSupportAccessCode]);

	useEffect(() => {
		setMobileLayout();
	}, [isMobileLayout]);

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			if (!event.data) return;
			if (event.data.subject === "SUPPORT_MODAL_IFRAME_LOADED") {
				setAccessCode();
			}
			if (event.data.subject === "SUPPORT_MODAL_STATE_CHANGE") {
				const state = event.data.state;
				setSupportModalState(state);
				if (state === "hidden") {
					setOpen(false);
				} else {
					setOpen(true);
				}
			}
			if (event.data.subject === "RESIZE_LIVE_CHAT") {
				const { width, height } = event.data;
				if (width > 0 && height > 0) {
					setChatDimensions({ width, height });
				}
			}
		};

		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [setAccessCode]);

	return (
		<iframe
			ref={supportModalIframeRef}
			src={supportModalIframeSrc}
			onLoad={() => setOpen(false)}
			style={{
				pointerEvents: supportModalState !== "hidden" ? "auto" : "none",
				width:
					supportModalState === "hidden" ? "0px" : !isChat || isMobileLayout ? "100%" : `${chatDimensions.width}px`,
				height:
					supportModalState === "hidden" ? "0px" : !isChat || isMobileLayout ? "100%" : `${chatDimensions.height}px`,
				border: "0",
				position: "fixed",
				top: isChat ? "auto" : 0,
				left: isChat ? "auto" : 0,
				bottom: !isChat || isMobileLayout ? "0px" : "10px",
				right: !isChat || isMobileLayout ? "0px" : "10px",
				zIndex: 1000,
			}}
		/>
	);
}
